import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wallet, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';
import { GRNPaymentsDialog } from '@/components/grn/GRNPaymentsDialog';
import { VPFilters } from '@/components/vendor-payments/VPFilters';
import { VPCards, VPAgeing } from '@/components/vendor-payments/VPCards';
import { VPInvoiceTable, VPVendorTable } from '@/components/vendor-payments/VPTables';
import { API, auth, quickRange, dmy } from '@/components/vendor-payments/vpUtils';

const BLANK = { quick: 'this_month', ...quickRange('this_month'), search: '', vendor_id: '', payment_status: '', payment_type: '', bucket: '' };
const clean = (f) => Object.fromEntries(Object.entries(f).filter(([k, v]) => k !== 'quick' && v));

export default function VendorPaymentSummary() {
  const [draft, setDraft] = useState(BLANK);
  const [applied, setApplied] = useState(BLANK);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('invoices');
  const [openGrn, setOpenGrn] = useState(null);
  const [vendorList, setVendorList] = useState([]);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  useEffect(() => {
    axios.get(`${API}/suppliers`, auth()).then((r) => setVendorList((r.data || []).map((s) => ({ vendor_id: s.id, vendor_name: s.name }))
      .sort((a, b) => a.vendor_name.localeCompare(b.vendor_name)))).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/vendor-payments/report`, { ...auth(), params: clean(applied) })
      .then((r) => setData(r.data)).catch(() => toast.error('Could not load vendor payments')).finally(() => setLoading(false));
  }, [applied]);
  useEffect(() => { load(); }, [load]);

  const apply = (patch = {}) => { const f = { ...draft, ...patch }; setDraft(f); setApplied(f); };
  const drill = (patch) => { apply(patch); setTab('invoices'); };
  const download = (report) => axios.get(`${API}/vendor-payments/export.xlsx`, { ...auth(), params: { ...clean(applied), report }, responseType: 'blob' })
    .then((r) => { const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([r.data])); a.download = `vendor_payment_${report}_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`; a.click(); })
    .catch(() => toast.error('Export failed'));
  const openRow = (r) => setOpenGrn({ id: r.grn_id, grn_number: r.grn_number, supplier_name: r.vendor_name, invoice_number: r.invoice_number });

  return (
    <div className="space-y-5" data-testid="vendor-payment-summary">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900"><Wallet className="h-6 w-6 text-blue-600" />Vendor Payment Summary</h1>
          <p className="text-sm text-slate-500">From GRN History & recorded GRN payments · outstanding and ageing as of {dmy(data?.as_of)} · one row per GRN</p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="vp-exports">
          {[['vendors', 'Vendor Summary'], ['invoices', 'Invoice Payments'], ['ageing', 'Ageing']].map(([k, l]) => (
            <div key={k} className="flex gap-1">
              <Button variant="outline" size="sm" onClick={() => download(k)} className="gap-1.5" data-testid={`vp-export-${k}`}><Download className="h-4 w-4" />{l}</Button>
              <CsvDownloadButton onDownload={() => download(k)} testId={`vp-export-${k}-csv`} />
            </div>
          ))}
        </div>
      </div>
      <VPFilters draft={draft} setDraft={setDraft} vendors={vendorList} onApply={() => apply()} onReset={() => { setDraft(BLANK); setApplied(BLANK); }} />
      {loading && !data ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <div className={`space-y-5 transition-opacity ${loading ? 'opacity-60' : ''}`}>
          <VPCards cards={data?.cards} onDrill={drill} />
          <VPAgeing ageing={data?.ageing} onDrill={drill} />
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList><TabsTrigger value="invoices" data-testid="vp-tab-invoices">Invoice-wise ({data?.rows?.length || 0})</TabsTrigger>
              <TabsTrigger value="vendors" data-testid="vp-tab-vendors">Vendor-wise ({data?.vendors?.length || 0})</TabsTrigger></TabsList>
            <TabsContent value="invoices"><VPInvoiceTable rows={data?.rows || []} onOpen={openRow} /></TabsContent>
            <TabsContent value="vendors"><VPVendorTable vendors={data?.vendors || []} onOpen={(v) => drill({ vendor_id: v.vendor_id })} /></TabsContent>
          </Tabs>
        </div>
      )}
      <GRNPaymentsDialog open={!!openGrn} grn={openGrn} isAdmin={user.role === 'admin'} onClose={() => setOpenGrn(null)} onChanged={() => load()} />
    </div>
  );
}
